import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { functionDefinitions } from './function-definitions.ts'
import { Modal } from './Modal.tsx'
import { Link } from './Router.tsx'
import { renderMarkdownDescription } from './markdown-renderer.tsx'
import syntaxDocumentation from './syntax-documentation.json'

interface DocsModalProps {
  isOpen: boolean
  onClose: () => void
}

type DocItem =
  | {
    type: 'function'
    name: string
    description: string
    parameters?: Array<{
      name: string
      type: string
      description?: string
      defaultValue?: number
    }>
    returnType?: string
    signature?: string
    examples?: string[]
    details?: string
  }
  | {
    type: 'syntax'
    name: string
    description: string
    parameters?: Array<{ name: string; type: string; description?: string }>
    returnType?: string
    signature?: string
    examples?: string[]
    details?: string
  }

export function DocsModal({ isOpen, onClose }: DocsModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set())
  const [highlightedFromToc, setHighlightedFromToc] = useState<string | null>(null)
  const docRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setExpandedDocs(new Set())
      setHighlightedFromToc(null)
    }
  }, [isOpen])

  const toggleDoc = (docId: string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev)
      if (next.has(docId)) {
        next.delete(docId)
      }
      else {
        next.add(docId)
      }
      return next
    })
  }

  const scrollToDoc = (docId: string) => {
    const element = docRefs.current.get(docId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setHighlightedFromToc(docId)
      setExpandedDocs(prev => {
        const next = new Set(prev)
        next.add(docId)
        return next
      })
    }
  }

  const dx7Functions = Object.values(functionDefinitions).filter(
    func => func.name.startsWith('dx7_') && !func.deprecated,
  )
  const dx7Representative = dx7Functions[0]

  const allFunctions: DocItem[] = [
    ...Object.values(functionDefinitions)
      .filter(func => !func.deprecated && !func.name.startsWith('dx7_'))
      .map(func => ({
        type: 'function' as const,
        name: func.name,
        description: func.description || '',
        parameters: func.parameters.map(p => ({
          name: p.name,
          type: p.type || 'number',
          description: p.description,
          defaultValue: p.defaultValue,
        })),
        returnType: func.returnType,
        examples: 'examples' in func && Array.isArray(func.examples) ? func.examples : undefined,
      })),
    ...(dx7Representative
      ? [
        {
          type: 'function' as const,
          name: 'dx7_[1..32]',
          description:
            'DX7 algorithms 1-32: 6-operator FM synthesis with various routing configurations. Each algorithm (dx7_1 through dx7_32) has a different operator arrangement.',
          parameters: dx7Representative.parameters.map(p => ({
            name: p.name,
            type: p.type || 'number',
            description: p.description,
            defaultValue: p.defaultValue,
          })),
          returnType: dx7Representative.returnType,
          examples: [
            'dx7_1(440, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1)',
            'dx7_5(220, 0.5, 1, 2, 0.5, 1, 1, 1, 1, 1, 1)',
            'dx7_32(880, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1)',
          ],
        },
      ]
      : []),
  ].sort((a, b) => a.name.localeCompare(b.name))

  const allSyntax: DocItem[] = syntaxDocumentation.syntax.map(syntax => ({
    type: 'syntax' as const,
    name: syntax.name,
    description: syntax.description,
    parameters: syntax.parameters,
    returnType: syntax.returnType,
    signature: syntax.signature,
    examples: syntax.examples,
    details: syntax.details,
  }))

  const allDocs: DocItem[] = [...allFunctions, ...allSyntax].sort((a, b) => a.name.localeCompare(b.name))

  const fuzzyMatch = (text: string, query: string): boolean => {
    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    if (!query) return true
    let queryIndex = 0
    for (let i = 0; i < lowerText.length && queryIndex < lowerQuery.length; i++) {
      if (lowerText[i] === lowerQuery[queryIndex]) {
        queryIndex++
      }
    }
    return queryIndex === lowerQuery.length
  }

  const matchesDoc = (doc: DocItem, query: string): boolean => {
    if (!query) return true
    if (doc.name === 'dx7_[1..32]') {
      const lowerQuery = query.toLowerCase()
      if (
        lowerQuery === 'dx7'
        || lowerQuery.startsWith('dx7_')
        || lowerQuery.includes('dx7')
        || fuzzyMatch('dx7 algorithm', lowerQuery)
      ) {
        return true
      }
    }
    const searchableText = [
      doc.name,
      doc.description,
      doc.signature || '',
      doc.details || '',
      ...(doc.examples || []),
    ].join(' ')
    return fuzzyMatch(searchableText, query)
  }

  const isExactNameMatch = (doc: DocItem, query: string): boolean => {
    if (!query) return false
    const lowerName = doc.name.toLowerCase()
    const lowerQuery = query.toLowerCase()
    if (doc.name === 'dx7_[1..32]') {
      return (
        lowerQuery === 'dx7'
        || lowerQuery.startsWith('dx7_')
        || lowerName.startsWith(lowerQuery)
        || lowerName.includes(lowerQuery)
      )
    }
    return (
      lowerName === lowerQuery
      || lowerName.startsWith(lowerQuery)
      || lowerName.includes(` ${lowerQuery}`)
      || lowerName.includes(`_${lowerQuery}`)
    )
  }

  const isExactDescriptionMatch = (doc: DocItem, query: string): boolean => {
    if (!query) return false
    const lowerDescription = doc.description.toLowerCase()
    const lowerQuery = query.toLowerCase()
    return (
      lowerDescription === lowerQuery
      || lowerDescription.includes(` ${lowerQuery} `)
      || lowerDescription.startsWith(`${lowerQuery} `)
      || lowerDescription.endsWith(` ${lowerQuery}`)
      || lowerDescription.includes(`.${lowerQuery}`)
      || lowerDescription.includes(`(${lowerQuery}`)
      || lowerDescription.includes(`-${lowerQuery}`)
    )
  }

  const docs = allDocs
    .filter(doc => matchesDoc(doc, searchQuery))
    .sort((a, b) => {
      const aExactName = isExactNameMatch(a, searchQuery)
      const bExactName = isExactNameMatch(b, searchQuery)
      const aExactDesc = isExactDescriptionMatch(a, searchQuery)
      const bExactDesc = isExactDescriptionMatch(b, searchQuery)

      if (aExactName && !bExactName) return -1
      if (!aExactName && bExactName) return 1
      if (aExactDesc && !bExactDesc) return -1
      if (!aExactDesc && bExactDesc) return 1
      return a.name.localeCompare(b.name)
    })

  const formatSignature = (doc: DocItem) => {
    if (doc.signature) return doc.signature
    if (doc.parameters && doc.parameters.length > 0) {
      const params = doc.parameters.map(p => p.name).join(', ')
      return `${doc.name}(${params})`
    }
    return doc.name
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-[90dvw]"
      maxWidth="max-w-[1400px]"
      className="bg-[#2a2a2a] h-[90dvh] flex flex-col"
      contentClassName="!p-0 !space-y-0 flex-1 flex flex-col overflow-hidden"
      overlayClassName="bg-black/90"
    >
      <div className="flex flex-col gap-4 p-6 border-b border-[#444] shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 flex items-center">
            <MagnifyingGlassIcon
              weight="regular"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search functions and syntax..."
              className="w-full pl-10 pr-4 py-2 bg-[#1a1a1a] border border-[#444] rounded-md text-white placeholder-[#666] focus:outline-none focus:border-orange-500"
              autoFocus
            />
          </div>
          or
          <div className="flex items-center gap-2">
            <Link
              href="/tutorials"
              onClick={onClose}
              className="px-4 py-2 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 text-sm font-medium"
            >
              Read the Tutorials
            </Link>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#333] rounded-md transition-colors"
              aria-label="Close"
            >
              <XIcon weight="regular" size={24} className="text-white" />
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 overflow-auto p-6">
          {docs.length === 0
            ? (
              <div className="text-center py-12 text-[#888]">
                No documentation found matching "{searchQuery}"
              </div>
            )
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map(doc => {
                  const docId = `${doc.type}-${doc.name}`
                  return (
                    <div
                      key={docId}
                      ref={el => {
                        if (el) {
                          docRefs.current.set(docId, el)
                        }
                        else {
                          docRefs.current.delete(docId)
                        }
                      }}
                      onClick={() => {
                        if (
                          doc.details
                          || (doc.parameters && doc.parameters.length > 0)
                          || (doc.examples && doc.examples.length > 0)
                        ) {
                          toggleDoc(docId)
                        }
                      }}
                      className={`bg-[#1a1a1a] rounded-lg p-3 transition-colors ${
                        highlightedFromToc === docId
                          ? 'border-2 border-blue-500'
                          : 'border border-[#444] hover:border-orange-500'
                      } ${
                        doc.details
                          || (doc.parameters && doc.parameters.length > 0)
                          || (doc.examples && doc.examples.length > 0)
                          ? 'cursor-pointer'
                          : ''
                      }`}
                    >
                      <div className={expandedDocs.has(docId) ? 'mb-2' : ''}>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-orange-500 font-mono leading-tight">
                            {formatSignature(doc)}
                          </h3>
                        </div>
                        {renderMarkdownDescription(doc.description, 'text-xs text-[#ccc] leading-tight')}
                      </div>
                      {expandedDocs.has(docId) && (
                        <>
                          {doc.details && (
                            <div className="mt-2 pt-2 border-t border-[#333]">
                              {renderMarkdownDescription(doc.details, 'text-sm text-[#999] leading-tight')}
                            </div>
                          )}
                          {doc.parameters && doc.parameters.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-[#333]">
                              <div className="text-xs text-[#888] mb-1.5 leading-tight">
                                Parameters:
                              </div>
                              <div className="space-y-1.5">
                                {doc.parameters.map((param, idx) => (
                                  <div key={idx} className="text-xs leading-tight">
                                    <span className="text-orange-400 font-mono">{param.name}</span>
                                    {'defaultValue' in param
                                      && param.defaultValue !== undefined && (
                                      <span className="text-[#666]">= {param.defaultValue}</span>
                                    )}
                                    {param.description && (
                                      <div className="text-[#999] mt-0.5 ml-2 leading-tight">
                                        {param.description}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {doc.examples && doc.examples.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-[#333]">
                              <div className="text-xs text-[#888] mb-1.5 leading-tight">
                                Examples:
                              </div>
                              <div className="space-y-1">
                                {doc.examples.map((example, idx) => (
                                  <div
                                    key={idx}
                                    className="text-xs font-mono text-[#aaa] bg-[#0a0a0a] p-2 rounded leading-tight"
                                  >
                                    {example}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
        </div>
        <div className="w-64 border-l border-[#444] overflow-y-auto shrink-0">
          <div className="p-0">
            <h2 className="text font-semibold text-[#888] mb-3 uppercase tracking-wide sticky p-4 top-0 bg-[#2a2a2a] border-b border-[#444]">
              Table of Contents
            </h2>
            <div className="pb-4">
              {docs.map(doc => {
                const docId = `${doc.type}-${doc.name}`
                return (
                  <button
                    key={docId}
                    onClick={() => scrollToDoc(docId)}
                    className="w-full text-left px-4 py-0.5 text-sm font-mono text-[#aaa] hover:text-orange-500 hover:bg-[#333] rounded transition-colors flex items-center gap-2"
                  >
                    {doc.type === 'syntax' && <span className="text-[8px] text-blue-400">S</span>}
                    <span className="truncate">{doc.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
